import { Icons, List, ListItem, Stack, ThemeSpaceVariable } from "@caspeco/casper-ui-library";
import {
    useAvailableTimes,
    useFilteredAvailableTimes,
    useFilteredExperiences,
} from "api/api-hooks/use-available-times";
import { useExperienceView } from "api/api-hooks/use-experience-view";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import EmptyState from "components/empty-state";
import SomethingWrong from "components/error-components/something-wrong";
import ActivityListSkeleton from "components/selection-components/select-activity/activity-list-skeleton";
import { ActivityModal } from "components/booking-modal/activity-modal";
import { ExperienceModal } from "components/booking-modal/experience-modal";
import ActivityFooter, {
    ACTIVITY_FOOTER_HEIGHT,
} from "components/selection-components/select-activity/select-activity-footer";
import SummaryAside from "components/selection-components/summary/summary-aside";
import SummaryModal from "components/selection-components/summary/summary-modal";
import { buildSearchParams } from "components/stepper/use-booking-navigate";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useMissingMenuSelection } from "hooks/use-missing-menu-selection";
import { useResolvedGuests } from "hooks/use-resolved-guests";
import { error } from "logger/logger";
import { useStepTracking } from "logger/use-step-tracking";
import { usePageTracking } from "logger/use-page-tracking";
import { PageType } from "logger/page-tracking";
import { TrackingStepName } from "logger/user-tracking";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Routes } from "routes";
import { UrlParamName } from "search-params";
import { AvailableTimesSection } from "types/available-times";
import {
    getExperienceAvailabilityState,
    getAvailabilityStateForBadge,
} from "utils/availability-utils";
import ActivityItem from "./activity-item";
import ExperienceItem from "components/experiences/experience-item";
import { NudgeHeader } from "components/selection-components/nudge-header";
import { Experience, getFamilyId } from "types/experience";
import { dedupeByFamily, getCatalogueFamilyIds } from "utils/experience-family";

function SelectActivity() {
    const { t } = useTranslation();
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [openExperience, setOpenExperience] = useState<Experience | null>(null);
    // The experience a suggestion was followed from, so following one is not a one-way door.
    const [suggestionOrigin, setSuggestionOrigin] = useState<Experience | null>(null);
    const [openActivity, setOpenActivity] = useState<AvailableTimesSection | null>(null);
    const [openActivityOnMenuStep, setOpenActivityOnMenuStep] = useState(false);
    const state = useBookingStateContext();
    const { date } = state;
    const { data: booking } = useWebBooking();
    const availableTimes = useAvailableTimes(date ?? booking?.start.clone().startOf("day"));
    const location = useLocation();
    const filteredAvailableTimes = useFilteredAvailableTimes(
        date ?? booking?.start.clone().startOf("day")
    );
    const filteredExperiences = useFilteredExperiences(
        date ?? booking?.start.clone().startOf("day")
    );

    // On a catalogue error the list still renders, ungated — showing everything degrades better
    // than showing nothing.
    const { data: experienceViewItems, isLoading: isLoadingCatalogue } = useExperienceView();

    const navigate = useNavigate();
    const ruleIdMissingMenuSelection = useMissingMenuSelection();
    const resolvedGuests = useResolvedGuests();

    const experienceIdParam = new URLSearchParams(location.search).get(UrlParamName.EXPERIENCE_ID);
    const autoOpenExperienceId = experienceIdParam ? parseInt(experienceIdParam, 10) : undefined;
    const hasAutoOpenedRef = useRef(false);

    useEffect(() => {
        if (
            !autoOpenExperienceId ||
            hasAutoOpenedRef.current ||
            availableTimes.isLoading ||
            filteredExperiences.length === 0
        ) {
            return;
        }

        // The booking-request handoff carries the catalogue's id, which is the family's; the row
        // holding the rules on this date is usually a copy with an id of its own.
        const experience = filteredExperiences.find(
            (exp) => exp.id === autoOpenExperienceId || getFamilyId(exp) === autoOpenExperienceId
        );
        if (!experience) {
            return;
        }

        hasAutoOpenedRef.current = true;
        setOpenExperience(experience);
    }, [autoOpenExperienceId, availableTimes.isLoading, filteredExperiences]);

    // Track time spent on the entire step
    useStepTracking({
        stepName: TrackingStepName.ActivityStep,
        autoTrack: true,
    });

    usePageTracking({
        pageName: "SelectActivity",
        pageType: PageType.WizardStep,
        metadata: {
            availableActivities: filteredAvailableTimes.length,
            bookedActivities: booking?.articles?.length ?? 0,
            hasExistingBooking: Boolean(booking),
        },
    });

    // Track activity item list viewing - but pause when modals are open
    const { stopTracking, restartTracking } = useStepTracking({
        stepName: TrackingStepName.ActivityItemList,
        autoTrack: true,
    });

    // Stop tracking when a modal opens, restart when it closes
    useEffect(() => {
        if (openActivity || summaryOpen || openExperience) {
            // Modal is open - stop tracking the activity list
            stopTracking();
        } else {
            restartTracking();
        }
    }, [openActivity, summaryOpen, openExperience, stopTracking, restartTracking]);

    const handleOpenActivity = (sectionOrRuleId: AvailableTimesSection | number): boolean => {
        let section: AvailableTimesSection | undefined;

        if (typeof sectionOrRuleId === "number") {
            section = filteredAvailableTimes.find((timeSection) =>
                timeSection.timeSets.some((timeSet) => timeSet.id === sectionOrRuleId)
            );

            if (!section) {
                error("Could not find activity to open", sectionOrRuleId);
                return false;
            }
        } else {
            section = sectionOrRuleId;
        }

        setOpenActivity(section);
        return true;
    };

    /** Callers address an experience by the row's own id or by its family; both must resolve. */
    const findExperience = (experienceId: number) =>
        filteredExperiences.find(
            (exp) => exp.id === experienceId || getFamilyId(exp) === experienceId
        );

    /**
     * Reopens whatever owns the unanswered menu on its menu step. A booked rule is either part of
     * a section or of an experience — experience rules are not in the sections list, so both have
     * to be looked up. Returns false when neither can be found.
     */
    const openMenuStepFor = (ruleId: number): boolean => {
        const section = filteredAvailableTimes.find((timeSection) =>
            timeSection.timeSets.some((timeSet) => timeSet.id === ruleId)
        );
        if (section) {
            setOpenActivityOnMenuStep(true);
            setOpenActivity(section);
            return true;
        }

        // webBookingRules carry the family id, which is not the id of the row that owns the rules.
        const experienceId = booking?.webBookingRules?.find(
            (rule) => rule.id === ruleId
        )?.experienceId;
        const experience = experienceId ? findExperience(experienceId) : undefined;
        if (experience) {
            setOpenActivityOnMenuStep(true);
            setOpenExperience(experience);
            return true;
        }

        error("Could not find activity or experience to open for menu step", ruleId);
        return false;
    };

    const gotoDetailsForm = () => {
        // A booked activity can require a menu selection that has not been made yet — the
        // modal's menu step is lost whenever it unmounts (browser back/forward, Esc, reload).
        // Reopen it straight on the menu step instead of going on to the details form, falling
        // back to the summary when it is not in the current day's available times.
        if (ruleIdMissingMenuSelection !== undefined) {
            if (!openMenuStepFor(ruleIdMissingMenuSelection)) setSummaryOpen(true);
            return;
        }

        const pathname = location.pathname.replace(Routes.AVAILABILITY, Routes.FINALIZE);
        setOpenActivity(null);
        const search = buildSearchParams(
            {
                guests: null,
                guestsChildren: null,
                date: null,
            },
            location.search
        );
        void navigate({
            pathname,
            search,
        });
    };

    // Wait for the catalogue too: without it the list cannot tell which families are offered, and
    // rendering first would show experiences that are about to be filtered away.
    if (availableTimes.isLoading || isLoadingCatalogue) {
        return <ActivityListSkeleton />;
    }
    if (availableTimes.isError) {
        return <SomethingWrong />;
    }

    const sectionsToRender = filteredAvailableTimes.filter((section) => {
        // A closed text row has nothing to say on this date, so it alone does not list the section.
        if (
            section.timeSets.length > 0 &&
            section.timeSets.every((timeSet) => timeSet.isTextRow && timeSet.isClosed)
        ) {
            return false;
        }
        if (!booking?.articles || booking.articles.length === 0) {
            return true;
        }
        return booking?.articles.every((article) => article.sectionId !== section.id);
    });

    const bookableExperiences = filteredExperiences.filter((experience) => {
        // Filter out unavailable experiences (e.g., those without inquiry booking type when in request mode)
        if (experience.unavailable) {
            return false;
        }

        const flatMappedSectionIds = experience.activities.flatMap(
            (activity) => activity.sectionId
        );
        return !booking?.articles.some((article) => {
            if (!article.sectionId) return false;

            return flatMappedSectionIds.includes(article.sectionId);
        });
    });

    // availableTimes accepts a variant on its own status, so it also returns copies whose family
    // the catalogue does not offer, and returns parent and copy separately (BOOK-3435, BOOK-3910).
    const catalogueFamilyIds = getCatalogueFamilyIds(experienceViewItems);
    const experiencesToRender = dedupeByFamily(
        bookableExperiences.filter(
            (experience) =>
                catalogueFamilyIds === null || catalogueFamilyIds.has(getFamilyId(experience))
        )
    );

    const handleOpenExperience = (experienceId: number) => {
        const experience = findExperience(experienceId);
        if (!experience) {
            error("Could not find experience to open", experienceId);
            return;
        }
        setOpenExperience(experience);
    };

    const hasContent = sectionsToRender.length > 0 || experiencesToRender.length > 0;

    const sectionList = !hasContent ? (
        <EmptyState
            icon={Icons.Smile} // Todo: change to PartyHorn when available
            description={t("selectActivity.noActivityDescription")}
            header={t("selectActivity.noActivityHeader")}
            pt={ThemeSpaceVariable.None}
        />
    ) : (
        <List>
            {experiencesToRender.map((experience) => (
                <ListItem key={`experience-${experience.id}`}>
                    <ExperienceItem
                        experience={experience}
                        onClick={() => handleOpenExperience(experience.id)}
                        availabilityState={getExperienceAvailabilityState(
                            experience,
                            resolvedGuests,
                            !!state.isBookingRequest
                        )}
                    />
                </ListItem>
            ))}
            {sectionsToRender.map((section) => (
                <ListItem key={section.id}>
                    <ActivityItem
                        section={section}
                        onClick={() => handleOpenActivity(section)}
                        availabilityState={getAvailabilityStateForBadge(
                            section.timeSets,
                            resolvedGuests,
                            !!state.isBookingRequest
                        )}
                    />
                </ListItem>
            ))}
        </List>
    );

    return (
        <>
            {(booking?.articles?.length ?? 0) > 0 && Boolean(sectionsToRender.length) && (
                <NudgeHeader />
            )}

            <Stack
                flexDirection="column"
                px={ThemeSpaceVariable.Medium}
                spacing={ThemeSpaceVariable.None}
                pb={{
                    // Prevents the last activity from being hidden behind the fixed ActivityFooter on mobile.
                    // See ACTIVITY_FOOTER_HEIGHT in select-activity-footer.tsx for the derivation.
                    base:
                        (booking?.articles?.length ?? 0) > 0
                            ? ACTIVITY_FOOTER_HEIGHT
                            : ThemeSpaceVariable.None,
                    md: ThemeSpaceVariable.None,
                }}
            >
                {sectionList}
                <SummaryModal
                    openActivity={handleOpenActivity}
                    openExperience={handleOpenExperience}
                    isOpen={summaryOpen}
                    onClose={() => setSummaryOpen(false)}
                    gotoDetailsForm={gotoDetailsForm}
                />
                {booking?.articles?.length && (
                    <SummaryAside
                        handleOpenActivity={handleOpenActivity}
                        handleOpenExperience={handleOpenExperience}
                        gotoDetailsForm={gotoDetailsForm}
                    />
                )}
                <ActivityModal
                    section={openActivity}
                    setSummaryOpen={setSummaryOpen}
                    onClose={() => {
                        setOpenActivity(null);
                        setOpenActivityOnMenuStep(false);
                    }}
                    openOnMenuStep={openActivityOnMenuStep}
                />
                <ExperienceModal
                    experience={openExperience}
                    onClose={() => {
                        setOpenExperience(null);
                        setSuggestionOrigin(null);
                        setOpenActivityOnMenuStep(false);
                    }}
                    openOnMenuStep={openActivityOnMenuStep}
                    onSelectExperience={(experienceId) => {
                        setSuggestionOrigin(openExperience);
                        handleOpenExperience(experienceId);
                    }}
                    onReturnToOrigin={
                        suggestionOrigin
                            ? () => {
                                  setOpenExperience(suggestionOrigin);
                                  setSuggestionOrigin(null);
                              }
                            : undefined
                    }
                />
                <ActivityFooter setSummaryOpen={setSummaryOpen} gotoDetailsForm={gotoDetailsForm} />
            </Stack>
        </>
    );
}

export { SelectActivity };
